import React from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CheckCircle2,
  Info,
  X,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";

const TYPE_STYLES = {
  success: {
    icon: CheckCircle2,
    accent: "border-l-sidebar-primary",
    iconWrap: "bg-sidebar-primary/10 text-sidebar-primary",
  },
  info: {
    icon: Info,
    accent: "border-l-sidebar-primary",
    iconWrap: "bg-sidebar-primary/10 text-sidebar-primary",
  },
  warning: {
    icon: AlertTriangle,
    accent: "border-l-amber-500",
    iconWrap: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  error: {
    icon: XCircle,
    accent: "border-l-destructive",
    iconWrap: "bg-destructive/10 text-destructive",
  },
};

const ToastItem = ({ toast, onDismiss }) => {
  const navigate = useNavigate();
  const styles = TYPE_STYLES[toast.type] || TYPE_STYLES.info;
  const Icon = toast.variant === "notification" ? Bell : styles.icon;

  const handleAction = () => {
    if (toast.href) {
      navigate(toast.href);
    }
    onDismiss(toast.id);
  };

  const isRich = toast.variant === "notification";

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border border-border/80 bg-card text-card-foreground shadow-lg",
        "border-l-4 animate-toast-in",
        styles.accent,
        toast.exiting && "opacity-0 translate-x-2 transition-all duration-200"
      )}
    >
      <div className="flex items-start gap-3 p-3.5">
        <div
          className={cn(
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            styles.iconWrap
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          {isRich ? (
            <>
              {toast.category ? (
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sidebar-primary">
                  {toast.category}
                </p>
              ) : null}
              {toast.title ? (
                <p className="mt-0.5 text-sm font-semibold leading-snug text-foreground">
                  {toast.title}
                </p>
              ) : null}
              {toast.description ? (
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-3">
                  {toast.description}
                </p>
              ) : null}
              {toast.href ? (
                <button
                  type="button"
                  onClick={handleAction}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-sidebar-primary hover:text-sidebar-primary/80 transition-colors"
                >
                  {toast.actionLabel || "View details"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-sm leading-snug text-foreground">
              {toast.message}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const ToastViewport = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed top-4 right-4 z-[4000] flex w-[min(100vw-2rem,24rem)] flex-col gap-2.5"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
};

export default ToastViewport;
